import { useDateRangeStore } from "./use-date-range-store";

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog/alert-dialog";
import { Button } from "@/components/ui/button/button";
import { Calendar } from "@/components/ui/calendar/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover/popover";
import { cn } from "@/lib/utils";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { SelectSingleEventHandler } from "react-day-picker";
import { useState } from "react";
import { toast } from "sonner";

export const DateRangePicker = () => {
  const { dateRange, setDateRange } = useDateRangeStore();

  const [open, setOpen] = useState(false);

  const [sD, setSD] = useState<Date | undefined>(() => dateRange?.from);
  const [cD, setCD] = useState<Date | undefined>(() => dateRange?.to);

  const handleSave = () => {
    if (!sD) {
      return toast.error("Select start date");
    }
    if (!cD) {
      return toast.error("Select end date");
    }

    setDateRange({
      from: sD,
      to: cD,
    });
    setOpen(false);
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger>
        <Button
          id="date"
          variant="outline"
          className={cn(
            "w-[260px] justify-start text-left font-normal",
            !dateRange && "text-muted-foreground"
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {sD ? (
            cD ? (
              <>
                {format(sD as Date, "LLL dd, y")} -{" "}
                {format(cD as Date, "LLL dd, y")}
              </>
            ) : (
              format(sD, "LLL dd, y")
            )
          ) : (
            <span>Pick a date</span>
          )}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Pick date range</AlertDialogTitle>
        </AlertDialogHeader>
        <div className="grid gap-4 py-4">
          <div className={"grid grid-cols-4 items-center gap-1"}>
            <label htmlFor="name" className={"text-left col-span-2"}>
              Start Date
            </label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "col-span-full justify-start text-left font-normal flex items-center gap-2 w-full",
                    !sD && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {sD ? format(sD, "PPP") : <span>Pick a date</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={sD as Date}
                  onSelect={setSD as SelectSingleEventHandler}
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className={"grid grid-cols-4 items-center gap-1"}>
            <label htmlFor="name" className={"text-left col-span-2"}>
              End Date
            </label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "col-span-full justify-start text-left font-normal flex items-center gap-2 w-full",
                    !cD && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {cD ? format(cD, "PPP") : <span>Pick a date</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={cD as Date}
                  onSelect={setCD as SelectSingleEventHandler}
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel
            onMouseDown={() => {
              setSD(dateRange?.from as Date);
              setCD(dateRange?.to as Date);
            }}
          >
            Cancel
          </AlertDialogCancel>
          <Button
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();

              handleSave();
            }}
          >
            Save
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
